import { c as _c } from "react/compiler-runtime";
import { type Ref, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Dialog, DialogActionPrimary, DialogActions, DialogClose, DialogContent, DialogControlledTrigger, DialogTrigger } from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { ProgressBar } from '@sola/ark-core/molecules/progress-bar/progress-bar.tsx';
import { TooltipInfo } from '@sola/ark-core/molecules/tooltip-info/tooltip-info.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { CartDrawerReturnFocusRef } from '@sola/ark-skippy/contexts/cart-drawer.tsx';
import type { GiftOption } from '../../types/gifts.ts';
interface GiftActionButtonProps {
  buttonText?: string;
  onClick: () => void;
  ref?: Ref<HTMLButtonElement>;
}
const GiftActionButton: React.FC<GiftActionButtonProps> = t0 => {
  const $ = _c(4);
  const {
    buttonText,
    onClick,
    ref
  } = t0;
  if (!buttonText) {
    return null;
  }
  let t1;
  if ($[0] !== buttonText || $[1] !== onClick || $[2] !== ref) {
    t1 = <DialogControlledTrigger><ButtonStyle variant="secondary" className="align-start" textwrap={true}><button ref={ref} type="button" onClick={onClick}>{buttonText}</button></ButtonStyle></DialogControlledTrigger>;
    $[0] = buttonText;
    $[1] = onClick;
    $[2] = ref;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
const GiftRemoveDialog: React.FC<{
  openTrigger: React.ReactElement;
  onRemove?: () => Promise<void>;
}> = ({
  openTrigger,
  onRemove
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);
  const {
    t
  } = useTranslation('my');
  const handleRemove = async () => {
    if (isSubmitting) {
      return;
    }
    setShowSubmitError(false);
    try {
      setIsSubmitting(true);
      await onRemove?.();
      setIsOpen(false);
    } catch {
      // Do not close the dialog, display the error to the user
      setShowSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Dialog open={isOpen} isBusy={isSubmitting} onOpenChange={(nextOpen: boolean) => {
    setIsOpen(nextOpen);
    setShowSubmitError(false);
  }}>
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent purpose="negative" icon="info-circle" title={t('my:giftCommitmentCard.removeGiftDialog.title')} supportingText={t('my:giftCommitmentCard.removeGiftDialog.supportingText')} size="sm">
        {showSubmitError && <Alert className="mb-comfy-lg" variant="negative" title={t('my:giftCommitmentCard.removeGiftDialog.error.title')} message={t('my:giftCommitmentCard.removeGiftDialog.error.message')} action={<Link href={t('my:ctas.contactSupport.url')}>
                {t('my:ctas.contactSupport.text')}
              </Link>} />}
        <DialogActions>
          <ButtonStyle className="w-full md:w-fit" size="md" purpose="neutral" variant="secondary" disabled={isSubmitting}>
            <DialogClose>
              {t('my:giftCommitmentCard.removeGiftDialog.cancel')}
            </DialogClose>
          </ButtonStyle>
          <DialogActionPrimary purpose="negative" onClick={handleRemove} isBusy={isSubmitting} disabled={isSubmitting}>
            {t('my:giftCommitmentCard.removeGiftDialog.remove')}
          </DialogActionPrimary>
        </DialogActions>
      </DialogContent>
    </Dialog>;
};
export const GiftCommitmentCardAction: React.FC<{
  giftOption: GiftOption;
  childName?: string;
  onGiftOptionsOpen: (returnFocusRef: CartDrawerReturnFocusRef) => void;
  onGiftRemove?: (cartItemId: string, returnFocusRef: CartDrawerReturnFocusRef) => Promise<void>;
}> = t0 => {
  const $ = _c(39);
  const {
    giftOption,
    childName,
    onGiftOptionsOpen,
    onGiftRemove
  } = t0;
  const actionRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    tooltip,
    progressLabel,
    buttonAction,
    cartItem,
    badge
  } = giftOption;
  const hasCartItem = Boolean(cartItem);
  const showStandaloneButton = Boolean(buttonAction) && !progressLabel && !hasCartItem && !tooltip;
  const showTooltipAction = tooltip && !hasCartItem && !progressLabel;
  const tooltipCount = tooltip?.count ?? progressLabel?.max;
  let t2;
  if ($[1] !== childName || $[2] !== progressLabel?.given || $[3] !== t || $[4] !== tooltip || $[5] !== tooltipCount) {
    const tooltipCountWord = tooltipCount === undefined ? undefined : t(`my:giftCommitmentCard.countWords.${tooltipCount}`, {
      defaultValue: String(tooltipCount)
    });
    t2 = tooltip ? t(`my:giftCommitmentCard.tooltipMessages.${tooltip.key}`, {
      childName,
      currentGifts: progressLabel?.given,
      count: tooltipCount,
      countWord: tooltipCountWord
    }) : undefined;
    $[1] = childName;
    $[2] = progressLabel?.given;
    $[3] = t;
    $[4] = tooltip;
    $[5] = tooltipCount;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const tooltipMessage = t2;
  let t3;
  if ($[7] !== buttonAction || $[8] !== t) {
    t3 = buttonAction ? t(`my:giftCommitmentCard.buttonText.${buttonAction}`) : undefined;
    $[7] = buttonAction;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const buttonText = t3;
  let t4;
  if ($[10] !== onGiftOptionsOpen) {
    t4 = () => onGiftOptionsOpen(actionRef);
    $[10] = onGiftOptionsOpen;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== buttonText || $[13] !== t4) {
    t5 = {
      buttonText,
      onClick: t4,
      ref: actionRef
    };
    $[12] = buttonText;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const actionButtonProps = t5;
  let t6;
  if ($[15] !== actionButtonProps || $[16] !== progressLabel || $[17] !== t || $[18] !== tooltipMessage) {
    t6 = progressLabel && <div className="flex flex-col gap-comfy-sm" style={{
      minWidth: 187
    }}><ProgressBar label={t(progressLabel.labelKey, {
        currentGifts: progressLabel.given,
        count: progressLabel.max,
        ordinal: t("core:numbers.suffix", {
          count: progressLabel.given,
          ordinal: true
        })
      })} value={progressLabel.given} minValue={0} maxValue={progressLabel.max} tooltipMessage={tooltipMessage} showValueLabel={false} /><GiftActionButton {...actionButtonProps} /></div>;
    $[15] = actionButtonProps;
    $[16] = progressLabel;
    $[17] = t;
    $[18] = tooltipMessage;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== actionButtonProps || $[21] !== showStandaloneButton) {
    t7 = showStandaloneButton && <GiftActionButton {...actionButtonProps} />;
    $[20] = actionButtonProps;
    $[21] = showStandaloneButton;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== actionButtonProps || $[24] !== badge || $[25] !== showTooltipAction || $[26] !== t || $[27] !== tooltipMessage) {
    t8 = showTooltipAction && <div className="flex gap-snug-lg">{badge && <Badge label={t(badge.label)} color={badge.color as 'positive' | 'brand'} iconBefore={badge.icon} />}<GiftActionButton {...actionButtonProps} /><TooltipInfo contentClassName="label-md lg:label-lg" align="end" size="sm" alignOffset={-12} sideOffset={-4} collisionPadding={16} defaultWrap={true}>{tooltipMessage}</TooltipInfo></div>;
    $[23] = actionButtonProps;
    $[24] = badge;
    $[25] = showTooltipAction;
    $[26] = t;
    $[27] = tooltipMessage;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== actionButtonProps || $[30] !== cartItem || $[31] !== onGiftRemove || $[32] !== t) {
    t9 = cartItem && <div className="flex flex-row items-center gap-comfy-sm label-potent label-xl">{t("core:numbers.currencyCustom", {
        amount: cartItem.amount,
        context: cartItem.frequency,
        useNegativeParens: true
      })}<GiftActionButton {...actionButtonProps} /><GiftRemoveDialog onRemove={async () => {
        if (!cartItem.cartId || !onGiftRemove) {
          return;
        }
        await onGiftRemove(cartItem.cartId, actionRef);
      }} openTrigger={<ButtonStyle size="lg" variant="tertiary" purpose="neutral" iconOnly={true}><button type="button"><ArkIcon aria-label={t("my:giftCommitmentCard.removeGiftDialog.remove")} iconName="trash-01" /></button></ButtonStyle>} /></div>;
    $[29] = actionButtonProps;
    $[30] = cartItem;
    $[31] = onGiftRemove;
    $[32] = t;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== t6 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t10 = <>{t6}{t7}{t8}{t9}</>;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
};